import { useState, useEffect } from "react";
import { HelmetProvider, Helmet } from "react-helmet-async";
import "@/App.css";
import "@/bookingflows-theme.css";
import "@/dark-theme.css";
import "@/superadmin-design-system.css";
import "@/responsive-design.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
// import { ClerkProvider } from '@clerk/react-router'; // Temporarily disabled
import LandingPage from "./pages/LandingPage";
import ErrorBoundary from "./components/ErrorBoundary";
import MarketplaceLanding from "./pages/MarketplaceLanding";
import MarketplaceSearch from "./pages/MarketplaceSearch";
import ExperienceDetail from "./pages/ExperienceDetail";
import OperatorProfile from "./pages/OperatorProfile";
import SupplierLandingPage from "./pages/SupplierLandingPage";
import PublicBlogPage from "./pages/PublicBlogPage";
import BlogListingPage from "./pages/BlogListingPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import SignupV3 from "./pages/SignupV3";
import SignupV2 from "./pages/SignupV2";
import SignupPage from "./pages/SignupPage";
import ForgotShortname from "./pages/ForgotShortname";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import VerifyTeamMember from "./pages/VerifyTeamMember";
import SetTeamMemberPassword from "./pages/SetTeamMemberPassword";
import ResetTeamPassword from "./pages/ResetTeamPassword";
import VerifyEmailChange from "./pages/VerifyEmailChange";
import RevertEmailChange from "./pages/RevertEmailChange";
import LoginV2 from "./pages/LoginV2";
import Login from "./pages/Login";
import PlatformAdminLogin from "./pages/PlatformAdminLogin";
import Dashboard from "./pages/DashboardV2";
import TeamMemberViewV2 from "./pages/TeamMemberViewV2";
import TeamMemberSignup from "./pages/TeamMemberSignup";
import TeamMemberLogin from "./pages/TeamMemberLogin";
import TeamMemberDashboard from "./pages/TeamMemberDashboard";
import SuperAdminDashboard from "./pages/SuperAdminDashboard";
import SuperAdminDashboardV2 from "./pages/SuperAdminDashboardV2";
import ProfilePage from "./pages/ProfilePage";
import SettingsPage from "./pages/SettingsPageNew";
import OrgSettingsPage from "./pages/OrgSettingsPage";
import AddTeamFlow from "./pages/AddTeamFlow"; // NEW: Smooth team addition flow
import AddAssetFlow from "./pages/AddAssetFlow"; // NEW: Smooth asset addition flow
import TeamLoginMinimal from "./pages/TeamLoginMinimal"; // NEW: Minimal team login for testing
import CustomerTicketPage from "./pages/CustomerTicketPage"; // NEW: Customer trip ticket view
import CustomerBookingWidget from "./pages/CustomerBookingWidget"; // NEW: Customer booking widget
import BookingCancelled from "./pages/BookingCancelled"; // NEW: Booking cancelled page
import PaymentCancelled from "./pages/PaymentCancelled"; // Payment cancelled page
import PartnerPortalPage from "./pages/PartnerPortalPage"; // Partner portal for monitoring payouts
import BookingPage from "./pages/BookingPage"; // NEW: Full booking flow page
import FlowBookingPage from "./pages/FlowBookingPage"; // NEW: Mini Flows booking page
import CheckInPage from "./pages/CheckInPage"; // NEW: QR code check-in page
import QRScannerGun from "./pages/QRScannerGun"; // NEW: QR Scanner Gun for staff
import SelfCheckIn from "./pages/SelfCheckIn"; // NEW: Customer self check-in portal
import VerifyEmail from "./pages/VerifyEmail"; // NEW: Email verification page
import PendingApproval from "./pages/PendingApproval"; // NEW: Pending approval page
import NotFoundPage from "./pages/NotFoundPage"; // NEW: 404 page
import MyTrips from "./pages/MyTrips"; // Customer profile & booking history
import BookingDetail from "./pages/BookingDetail"; // Customer booking detail / ticket
import CustomerAuthCallback from "./pages/CustomerAuthCallback"; // Customer Google OAuth callback
import CustomerReviewPage from "./pages/CustomerReviewPage"; // Customer review submission page
import MagicLinkCallback from "./pages/MagicLinkCallback"; // Magic link email verification
import { Toaster } from "./components/ui/sonner";
import { SettingsProvider } from "./contexts/SettingsContext";
import { CustomerAuthProvider } from "./contexts/CustomerAuthContext"; // Customer auth context
import CookieConsent, { applyExistingConsent } from "./components/CookieConsent";

// ScrollToTop component - resets scroll position on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

// Capacitor plugins for iOS/Android native app
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { initPushNotifications, registerForPushNotifications } from './utils/pushNotifications';
import { hapticSuccess } from './utils/haptics';

// Check if running as native app
const isNativeApp = Capacitor.isNativePlatform();

// Initialize Capacitor plugins for native apps
const initializeCapacitor = async () => {
  if (isNativeApp) {
    try {
      // Configure status bar for iOS - light content on blue background
      await StatusBar.setStyle({ style: Style.Light });
      
      // On Android, also set the background color
      if (Capacitor.getPlatform() === 'android') {
        await StatusBar.setBackgroundColor({ color: '#2563EB' });
      }
      
      // Initialize push notification listeners
      initPushNotifications({
        onRegistration: (token) => {
          // Token will be sent to backend when user logs in
          localStorage.setItem('pushToken', token);
        },
        onNotificationReceived: (notification) => {
          // Haptic feedback when notification arrives
          hapticSuccess();
        },
        onNotificationAction: (action) => {
          // Handle navigation based on notification data
          const data = action.notification.data;
          if (data?.type === 'new_booking' && data?.bookingId) {
            // Navigate to booking details - handled by the app's routing
            window.location.href = `/dashboard?booking=${data.bookingId}`;
          }
        },
      });
      
      // Request push notification permissions
      await registerForPushNotifications();
      
      // Hide splash screen after app is ready (with fade animation)
      await SplashScreen.hide({ fadeOutDuration: 500 });
    } catch (error) {
    }
  }
};

// Apply existing cookie consent on app load (for analytics, etc.) - ONLY on web
if (!isNativeApp) {
  applyExistingConsent();
}

// const CLERK_PUBLISHABLE_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY; // Temporarily disabled

// Protected Route Component with proper authentication state handling
const ProtectedRoute = ({ children, requiredType }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null); // null = loading, true/false = checked
  const [userType, setUserType] = useState(null);
  
  useEffect(() => {
    // Small delay to allow localStorage to persist after login
    const checkAuth = () => {
      const storedUserType = localStorage.getItem("userType");
      const token = localStorage.getItem("jwtToken"); // Fixed: was "token", should be "jwtToken"
      
      
      if (token && storedUserType) {
        setUserType(storedUserType);
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
      }
    };
    
    // Check immediately and after a small delay to handle race condition
    checkAuth();
    const timeoutId = setTimeout(checkAuth, 100);
    
    return () => clearTimeout(timeoutId);
  }, []);
  
  // Show loading state while checking authentication
  if (isAuthenticated === null) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    // Redirect super_admin to platform-admin login (uses PostgreSQL v2)
    if (requiredType === 'super_admin') {
      return <Navigate to="/platform-admin" replace />;
    }
    return <Navigate to="/login" replace />;
  }
  
  if (requiredType && userType !== requiredType) {
    // Redirect to appropriate login based on required type
    if (requiredType === 'super_admin') {
      return <Navigate to="/platform-admin" replace />;
    }
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Team Protected Route Component for teamMember authentication
const TeamProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  
  useEffect(() => {
    const checkAuth = () => {
      const teamToken = localStorage.getItem("teamToken");
      const teamData = localStorage.getItem("teamData");
      
      if (teamToken && teamData) {
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
      }
    };
    
    checkAuth();
    const timeoutId = setTimeout(checkAuth, 100);
    
    return () => clearTimeout(timeoutId);
  }, []);
  
  // Show loading state while checking authentication
  if (isAuthenticated === null) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/team/login" replace />;
  }
  
  return children;
};

// Component to handle native app startup - ensures app starts on marketplace
const NativeAppStartupHandler = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    if (isNativeApp) {
      const hasLaunchedBefore = sessionStorage.getItem('nativeAppLaunched');
      
      if (!hasLaunchedBefore) {
        sessionStorage.setItem('nativeAppLaunched', 'true');
        // Native app starts on /login for operators (Apple requirement)
        if (location.pathname === '/' || location.pathname === '') {
          navigate('/login', { replace: true });
        }
      }
    }
  }, [navigate, location.pathname]);
  
  return null;
};

function App() {
  // Temporarily disable Clerk to debug
  // if (!CLERK_PUBLISHABLE_KEY) {
  //   console.warn('Clerk publishable key not found - some features may not work');
  // }

  // Initialize Capacitor for native iOS/Android app
  useEffect(() => {
    initializeCapacitor();
  }, []);

  return (
    // <ClerkProvider publishableKey={CLERK_PUBLISHABLE_KEY} afterSignOutUrl="/login">
    <HelmetProvider>
      {/* Default SEO tags - pages can override these */}
      <Helmet>
        <title>BookingFlows | Online Booking Software for Tours, Venues & Experiences</title>
        <meta name="description" content="Online booking software for tours, venues, experiences & more. Take bookings 24/7, accept payments, manage staff scheduling, and send digital waivers." />
        <meta name="keywords" content="booking software, online booking system, tour booking software, venue booking, reservation system" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="BookingFlows | Online Booking Software" />
        <meta property="og:description" content="Take bookings online. Get paid automatically. Manage your team." />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <SettingsProvider>
        <CustomerAuthProvider>
        <div className="App">
          <BrowserRouter>
            <ScrollToTop />
            {/* Handle native app startup - redirect to marketplace */}
            {isNativeApp && <NativeAppStartupHandler />}
            <Routes>
              {/* Marketplace routes (customer-facing) */}
              <Route path="/" element={<MarketplaceLanding />} />
              <Route path="/search" element={<MarketplaceSearch />} />
              <Route path="/experience/:serviceId" element={<ExperienceDetail />} />
              <Route path="/host/:operatorId" element={<OperatorProfile />} />
              <Route path="/for-suppliers" element={<SupplierLandingPage />} />
              <Route path="/blog" element={<BlogListingPage />} />
              <Route path="/blog/:orgSlug" element={<PublicBlogPage />} />
              
              {/* Customer profile routes */}
              <Route path="/my-trips" element={<MyTrips />} />
              <Route path="/my-trips/callback" element={<CustomerAuthCallback />} />
              <Route path="/my-trips/:bookingId" element={<BookingDetail />} />
              <Route path="/auth/magic-link" element={<MagicLinkCallback />} />
              
              {/* Public routes - Native app starts on login, web starts on landing */}
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/customer-ticket/:ticketId" element={<CustomerTicketPage />} />
              <Route path="/ticket/:ticketId" element={<CustomerTicketPage />} /> {/* Alias for magic links */}
              <Route path="/:orgSlug/ticket/:ticketId" element={<CustomerTicketPage />} /> {/* Org-prefixed ticket URL */}
              <Route path="/m/:orgSlug/review" element={<CustomerReviewPage />} /> {/* Customer review page with /m/ prefix */}
              <Route path="/:orgSlug/review" element={<CustomerReviewPage />} /> {/* Customer review page */}
              <Route path="/check-in/:confirmationCode" element={<CheckInPage />} />
              <Route path="/self-check-in" element={<SelfCheckIn />} /> {/* Customer self check-in portal */}
              <Route path="/qr-code-scanner" element={<QRScannerGun />} /> {/* QR Scanner Gun for staff */}
              <Route path="/book/:orgSlug/:flowSlug" element={<ErrorBoundary name="FlowBooking"><FlowBookingPage /></ErrorBoundary>} />
              <Route path="/book" element={<ErrorBoundary name="BookingWidget"><CustomerBookingWidget /></ErrorBoundary>} />
              <Route path="/booking" element={<BookingPage />} />
              <Route path="/booking-cancelled" element={<BookingCancelled />} />
              <Route path="/payment-cancelled" element={<PaymentCancelled />} />
              <Route path="/signup" element={<SignupV3 />} />
              <Route path="/signup-v2" element={<SignupV2 />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/pending-approval" element={<PendingApproval />} />
              <Route path="/forgot-shortname" element={<ForgotShortname />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/verify-team-member" element={<VerifyTeamMember />} />
              <Route path="/set-team-password" element={<SetTeamMemberPassword />} />
              <Route path="/reset-team-password" element={<ResetTeamPassword />} />
              <Route path="/verify-email-change" element={<VerifyEmailChange />} />
              <Route path="/revert-email-change" element={<RevertEmailChange />} />
              <Route path="/login" element={<LoginV2 />} />
              <Route path="/login-legacy" element={<Login />} />
              <Route path="/platform-admin" element={<PlatformAdminLogin />} />
              <Route path="/teamMember-signup" element={<TeamMemberSignup />} />
              <Route path="/teamMember/login" element={<TeamMemberLogin />} />
              <Route path="/teamMember/dashboard" element={<TeamMemberDashboard />} />
              
              {/* Team member routes - TESTING WITH MINIMAL COMPONENT */}
              <Route path="/team/login" element={<TeamLoginMinimal />} />
              <Route path="/team/add" element={
                <ProtectedRoute>
                  <AddTeamFlow />
                </ProtectedRoute>
              } />
              <Route path="/asset/add" element={
                <ProtectedRoute>
                  <AddAssetFlow />
                </ProtectedRoute>
              } />
              
              {/* Protected routes */}
              <Route 
                path="/admin" 
                element={
                  <ProtectedRoute>
                    <ErrorBoundary name="Dashboard">
                      <Dashboard />
                    </ErrorBoundary>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/team-member" 
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/settings" 
                element={
                  <ProtectedRoute>
                    <SettingsPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/profile" 
                element={
                  <ProtectedRoute requiredType="admin">
                    <ProfilePage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/org-settings" 
                element={
                  <ProtectedRoute requiredType="admin">
                    <OrgSettingsPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/teamMember-view" 
                element={
                  <ProtectedRoute requiredType="teamMember">
                    <TeamMemberViewV2 />
                  </ProtectedRoute>
                } 
              />
              
              {/* Super Admin route */}
              <Route 
                path="/super-admin" 
                element={
                  <ProtectedRoute requiredType="super_admin">
                    <SuperAdminDashboardV2 />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/super-admin-legacy" 
                element={
                  <ProtectedRoute requiredType="super_admin">
                    <SuperAdminDashboard />
                  </ProtectedRoute>
                } 
              />
              
              {/* Partner Portal - public access with token */}
              <Route path="/partner-portal" element={<ErrorBoundary name="PartnerPortal"><PartnerPortalPage /></ErrorBoundary>} />
              <Route path="/partner-portal/:partnerId" element={<ErrorBoundary name="PartnerPortal"><PartnerPortalPage /></ErrorBoundary>} />
              
              {/* Redirect old routes */}
              <Route path="/teamMember-login" element={<Navigate to="/login" replace />} />
              <Route path="/partners" element={<Navigate to="/admin?tab=partners" replace />} />
              
              {/* 404 Catch-all - must be last */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </BrowserRouter>
          <Toaster />
          {/* Cookie consent - only show on web, not native apps */}
          {!isNativeApp && <CookieConsent />}
        </div>
        </CustomerAuthProvider>
      </SettingsProvider>
    </HelmetProvider>
    // </ClerkProvider>
  );
}

export default App;